import { Card, Box } from "@material-ui/core";
import classNames from "classnames";
import React from "react";
import { useTranslation } from "react-i18next";
import { useSnapshot } from "valtio";
import { CSSTransition } from "react-transition-group";
import { designStore, generalConfigStore } from "@store";

import { data } from "@pages/GeneralConfig/Design/Content/OrderTaking/fastFoodCard";

import { AddIcon } from "../Icons/AddIcon";
import { AllergenIcon } from "../Icons/AllergenIcon";
import { FireIcon } from "../Icons/FireIcon";
import { InfoIcon } from "../Icons/InfoIcon";
import "./index.css";
import { TagsSwiperWrapper } from "../TagsSwiperWrapper";

const CARD_STYLE = {
    height: "100%",
    width: "133px",
    display: "grid",
    gridTemplateRows: "70px 10px 20px 20px 20px",
} as const;

const CALORIE_TEXT_STYLE = {
    fontWeight: "normal",
    fontSize: "10px",
    fontFamily: "Segoe UI",
    textAlign: "center",
    marginTop: "5px",
} as const;

const NAME_STYLE = {
    font: "normal normal bold 9px/5px Segoe UI",
    textAlign: "center",
    width: "100%",
    height: "100%",
    color: "#343434",
    textTransform: "uppercase",
} as const;

const PRICE_STYLE = {
    textAlign: "center",
    width: "100%",
    height: "100%",
    color: "#343434",
    textTransform: "uppercase",
} as const;

const DESCRIPTION_STYLE = {
    textAlign: "center",
    color: "#000000",
    fontWeight: "normal",
    fontSize: "6px",
    fontFamily: "Segoe UI",
} as const;

type WorkflowProduct = {
    name: { isVisible: boolean; content: string };
    price: { isVisible: boolean; content: number | string };
    calorie: { isVisible: boolean; content: string };
    description: { isVisible: boolean; content: string };
    image: string;
};

type PreviewProductCardProps = {
    product: WorkflowProduct;
    globalColor: string;
    secondColor: string;
    isAllergenOrderTakingActive: boolean;
    isOrderTakingCaloryActive: boolean;
};

function PreviewProductCard({
    product,
    globalColor,
    secondColor,
    isAllergenOrderTakingActive,
    isOrderTakingCaloryActive,
}: PreviewProductCardProps): JSX.Element {
    return (
        <div
            className="d-flex justify-content-center align-items-center"
            style={{ zIndex: 1 }}
        >
            <Box>
                <Card style={CARD_STYLE}>
                    <div
                        className="d-flex flex-row justify-content-around align-items-center"
                        style={{ gap: "10px" }}
                    >
                        <img
                            alt={product.name.content}
                            src={product.image}
                            style={{ height: "60px" }}
                        />
                        <div
                            className="mt-4 d-flex flex-column justify-content-end align-items-center"
                            style={{ gap: "2px" }}
                        >
                            <InfoIcon width={15} />
                            {isAllergenOrderTakingActive === true ? (
                                <AllergenIcon
                                    globalColor={globalColor}
                                    width={15}
                                />
                            ) : null}
                            <AddIcon
                                secondColor={secondColor}
                                globalColor={globalColor}
                                width={30}
                            />
                        </div>
                    </div>

                    {isOrderTakingCaloryActive === true ? (
                        <div
                            className="d-flex justify-content-center align-items-center"
                            style={{ gap: "2px" }}
                        >
                            <FireIcon />
                            <span style={CALORIE_TEXT_STYLE}>
                                {product.calorie.content} {"cal"}
                            </span>
                        </div>
                    ) : null}

                    <span
                        className="d-flex justify-content-center align-items-center"
                        style={NAME_STYLE}
                    >
                        {product.name.content}
                    </span>

                    <span
                        className="d-flex justify-content-center align-items-center"
                        style={PRICE_STYLE}
                    >
                        {product.price.content} {"€"}
                    </span>

                    <span
                        className="d-flex justify-content-center align-items-center"
                        style={DESCRIPTION_STYLE}
                    >
                        {product.description.content}
                    </span>
                </Card>
            </Box>
        </div>
    );
}

function chunkArray<T>(arr: T[], size: number): T[][] {
    const chunks: T[][] = [];
    for (let i = 0; i < arr.length; i += size) {
        chunks.push(arr.slice(i, i + size));
    }
    return chunks;
}

export function MainContentWrapper(): JSX.Element {
    const { t } = useTranslation();
    const workflow = data.workFlow;

    const {
        project: {
            template: {
                content: { generalDesign },
                pages,
            },
        },
        secondColor,
        isAllergenOrderTakingActive,
        isOrderTakingCaloryActive,
        chosenTransition,
        isAnimationIn,
    } = useSnapshot(generalConfigStore);
    const { informationMessageCompositeProduct, breadcrumbTrail } = useSnapshot(
        designStore
    );

    const compositeProduct = (pages.ways["orderTaking"] as OrderTakingPageType)
        .compositeProduct;
    const globalColor = (generalDesign as GeneralDesignType).colors[0].content;

    return (
        <div
            className={classNames({
                "d-flex flex-column": breadcrumbTrail === true,
                "p-2": breadcrumbTrail === false,
            })}
            style={{ overflowY: "auto" }}
        >
            <div className="mb-1">
                {informationMessageCompositeProduct === true ? (
                    <div className="d-flex justify-content-center">
                        {t("Please choose your dish")}
                    </div>
                ) : null}

                {compositeProduct.isMinMaxShown ? (
                    <div className="d-flex justify-content-center">
                        ({t("Min 1 : Max 5")})
                    </div>
                ) : null}
            </div>
            {compositeProduct.isTags === true ? (
                <div
                    className="d-flex justify-content-start align-items-start"
                    style={{ minHeight: "30px" }}
                >
                    <TagsSwiperWrapper />
                </div>
            ) : (
                <div
                    className="d-flex justify-content-start align-items-start"
                    style={{ height: "30px" }}
                ></div>
            )}
            <CSSTransition
                in={isAnimationIn}
                appear={true}
                timeout={chosenTransition.timeout}
                classNames={chosenTransition.className}
            >
                <div
                    className="d-flex flex-column justify-content-center"
                    style={{
                        gap: "8px",
                        marginTop: "5px",
                    }}
                >
                    {chunkArray(workflow.product, 2).map(
                        (row, rowIndex) => (
                            <div
                                key={rowIndex}
                                className="d-flex flex-row justify-content-center"
                                style={{ gap: "20px" }}
                            >
                                {row.map((product) => (
                                    <PreviewProductCard
                                        key={product.name.content}
                                        product={product}
                                        globalColor={globalColor}
                                        secondColor={secondColor}
                                        isAllergenOrderTakingActive={
                                            isAllergenOrderTakingActive
                                        }
                                        isOrderTakingCaloryActive={
                                            isOrderTakingCaloryActive
                                        }
                                    />
                                ))}
                            </div>
                        )
                    )}
                </div>
            </CSSTransition>
        </div>
    );
}
